import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { api, errMsg } from "../lib/api";
import { useLang } from "../lib/i18n";
import { Field, useBusy } from "../components/common";

const EMPTY = { name: "", owner_name: "", email: "", phone: "", address: "", language: "en", plan: "basic", user_limit: "", storage_limit_mb: "", ai_limit_month: "" };

export function CompanyForm({ company, onClose, onSaved }) {
  const { t } = useLang();
  const [f, setF] = useState(company ? { ...EMPTY, ...company } : EMPTY);
  const [logo, setLogo] = useState(null);
  const [busy, run] = useBusy();
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
  const save = () => run(async () => {
    const body = { ...f, user_limit: num(f.user_limit), storage_limit_mb: num(f.storage_limit_mb), ai_limit_month: num(f.ai_limit_month) };
    try {
      const { data } = company ? await api.put(`/platform/companies/${company.id}`, body) : await api.post("/platform/companies", body);
      if (logo) { const fd = new FormData(); fd.append("file", logo); await api.post(`/platform/companies/${data.id}/logo`, fd); }
      if (!company) toast.success(data.invite_email_sent ? t("Company created. Invitation sent to the owner.", "Empresa creada. Invitación enviada al dueño.") : t("Company created, but the invitation email failed. Use Resend invite.", "Empresa creada, pero el correo falló. Use Reenviar invitación."));
      else toast.success(t("Company updated", "Empresa actualizada"));
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  });
  const inp = (k, l, type = "text", extra = {}) => <Field label={l}><input className="inp" type={type} value={f[k] ?? ""} onChange={set(k)} data-testid={`company-${k}-input`} {...extra} /></Field>;
  const sel = (k, l, opts) => <Field label={l}><select className="inp" value={f[k]} onChange={set(k)} data-testid={`company-${k}-select`}>{opts.map(([v, x]) => <option key={v} value={v}>{x}</option>)}</select></Field>;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="company-form-dialog">
        <DialogHeader>
          <DialogTitle>{company ? t("Edit company", "Editar empresa") : t("New company", "Nueva empresa")}</DialogTitle>
          <DialogDescription>{t("Each company gets its own completely separate workspace.", "Cada empresa tiene su propio espacio totalmente separado.")}</DialogDescription>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          {inp("name", t("Company name", "Nombre de la empresa"))}
          {inp("owner_name", t("Owner name", "Nombre del dueño"))}
          {inp("email", t("Owner email (login)", "Correo del dueño (inicio de sesión)"), "email", { disabled: !!company })}
          {inp("phone", t("Phone", "Teléfono"), "tel")}
          <div className="sm:col-span-2">{inp("address", t("Address", "Dirección"))}</div>
          {sel("language", t("Language", "Idioma"), [["en", "English"], ["es", "Español"]])}
          {sel("plan", t("Plan", "Plan"), [["basic", "Basic"], ["pro", "Pro"], ["business", "Business"]])}
          {inp("user_limit", t("User limit", "Límite de usuarios"), "number")}
          {inp("storage_limit_mb", t("Storage limit (MB)", "Límite de almacenamiento (MB)"), "number")}
          {inp("ai_limit_month", t("AI uses per month", "Usos de IA por mes"), "number")}
          <Field label={t("Logo (PNG/JPG)", "Logo (PNG/JPG)")}><input className="inp" type="file" accept="image/png,image/jpeg" onChange={(e) => setLogo(e.target.files[0])} data-testid="company-logo-input" /></Field>
        </div>
        <p className="text-xs text-slate-500">{t("Leave limits empty to use the plan defaults (Basic 3 users / 1 GB / 100 AI, Pro 10 / 5 GB / 500, Business unlimited).", "Deje los límites vacíos para usar los del plan (Basic 3 usuarios / 1 GB / 100 IA, Pro 10 / 5 GB / 500, Business ilimitado).")}</p>
        <button className="btn-primary" disabled={busy} onClick={save} data-testid="company-save-btn">{company ? t("Save changes", "Guardar cambios") : t("Create company & send invitation", "Crear empresa y enviar invitación")}</button>
      </DialogContent>
    </Dialog>
  );
}
